import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Alert, Typography } from '@mui/material';

import { useAppDispatch } from '@/app/store/hooks';
import { Loader } from '@/components/feedback/Loader';
import { DetailCard, DetailCardBody, DetailCardHeader } from '@/components/layout/DetailCard';
import {
  DETAIL_LABEL_CLASS,
  DetailField,
  DetailFieldGrid,
} from '@/components/layout/DetailField';
import {
  useGetGeneralDataQuery,
  useIsAuthenticated,
  useUpdateDirectorDinMutation,
  type GeneralData,
} from '@/features/auth';
import { toastShown } from '@/features/ui';

import { selectUniqueAddresses } from './address';
import { DirectorIdField } from './DirectorIdField';
import { fullNameOf } from './fullName';

/**
 * The director's own record.
 *
 * Everything here comes from the ASIC feed and only the Director ID is
 * writable, so the card has no edit mode of its own: it reads as a record, and
 * the one editable value carries its own control. Name, phone and email are
 * changed by the director's agent, which the card says outright — a disabled
 * input with no explanation reads as a broken form.
 */
export const PersonalDetailsCard = () => {
  const dispatch = useAppDispatch();
  // Skipped without a session, for the same reason `useCurrentUser` skips:
  // the record is director-scoped, so requesting it logged out 401s on every
  // render and can trip the middleware's session teardown.
  const isAuthenticated = useIsAuthenticated();
  const { data, isLoading } = useGetGeneralDataQuery(undefined, { skip: !isAuthenticated });
  const [updateDin, { isLoading: isSaving }] = useUpdateDirectorDinMutation();

  /** Resolves false on failure, which is what keeps the field in its editor
   *  holding what the director typed — a failed save should not cost them the
   *  value. */
  const handleSave = async (din: string) => {
    if (data === undefined) return false;

    try {
      await updateDin({ asicMemberId: data.asicMemberId, din }).unwrap();
      dispatch(toastShown('Director ID updated.', 'success'));
      return true;
    } catch {
      dispatch(toastShown('Your Director ID could not be saved. Please try again.', 'error'));
      return false;
    }
  };

  return (
    <DetailCard>
      <DetailCardHeader
        icon={<PersonOutlineIcon />}
        title="Personal details"
        subtitle="Your record as ASIC holds it"
      />

      <DetailCardBody className="flex flex-col gap-6 p-5 sm:p-6">
        {data === undefined ? (
          <Loader label="Loading your details" />
        ) : (
          <>
            <DetailFieldGrid>
              <DetailField label="Name" value={fullNameOf(data)} />
              <DetailField label="Phone number" value={data.phoneNumber} />
              <DetailField label="Email address" value={data.email} />
              <DirectorIdField
                directorId={data.directorId}
                onSave={handleSave}
                isSaving={isSaving}
              />
            </DetailFieldGrid>

            {/* A note, not an alert: nothing is wrong, and a filled panel here
                competed with the address section's genuine empty state. */}
            <div className="flex items-start gap-2 text-content-muted">
              <InfoOutlinedIcon fontSize="small" className="mt-px shrink-0" />
              <Typography variant="body2" color="text.secondary">
                To update contact info please get in touch with your agent.
              </Typography>
            </div>
          </>
        )}

        <PersonalAddress director={data} isLoading={isLoading} />
      </DetailCardBody>
    </DetailCard>
  );
};

interface PersonalAddressProps {
  director: GeneralData | undefined;
  isLoading: boolean;
}

/** Read-only in every mode: ASIC owns the address, and there is no endpoint to
 *  change it from here. */
const PersonalAddress = ({ director, isLoading }: PersonalAddressProps) => {
  const addresses = director === undefined ? [] : selectUniqueAddresses(director.addresses);

  return (
    <section className="flex flex-col gap-3 border-t border-divider pt-5">
      <div className="flex items-center gap-2">
        <HomeOutlinedIcon fontSize="small" className="text-content-muted" />
        <Typography
          variant="caption"
          component="h3"
          color="text.secondary"
          className={DETAIL_LABEL_CLASS}
        >
          Personal address
        </Typography>
      </div>

      {isLoading ? (
        <Loader size="inline" label="Loading your addresses" />
      ) : addresses.length === 0 ? (
        <Alert severity="info" className="rounded-lg">
          No address is recorded against your ASIC profile.
        </Alert>
      ) : (
        <ul className="flex flex-col gap-2">
          {addresses.map((address) => (
            <li
              key={address}
              className="rounded-lg border border-divider bg-surface-subtle px-3 py-2.5"
            >
              <Typography className="text-[15px] leading-snug break-words">
                {address}
              </Typography>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
